import React, { useState, useEffect, useLayoutEffect, useMemo } from "react";
import { Context, Hooks, Util } from "@xt/xtbase";

const { XtContext } = Context;
const { useTranslation } = Hooks;
const { getUrl } = Util;

import styles from "./index.module.less";
import giftImg from "assets/images/activity/moneyBox.png";
import { getPublicActivity } from "@/api/activity";
import {
  buildStepsFromRewards,
  getCompletedCount,
  pickPublishedFromList,
} from "../activityHomeShared";

function getMaxRewardAmountText(activity) {
  const rewards = activity?.rewards;
  if (!Array.isArray(rewards) || rewards.length === 0) return "";
  const rewardWithMaxAmount = rewards.find((reward) => reward?.maxRewardAmount != null && reward?.maxRewardAmount !== "");
  if (!rewardWithMaxAmount) return "";

  const amount = rewardWithMaxAmount.maxRewardAmount;
  const currency = String(rewardWithMaxAmount.rewardCurrency || "USDT").toUpperCase();
  return `${amount}${currency}`;
}

export default function HomeNewUserOverlay() {
  const t = useTranslation();
  const [appState] = React.useContext(XtContext);
  const { loginStatus } = appState;
  const notLoginConfirmed = loginStatus === 0 || loginStatus === 2;

  const [closedByUser, setClosedByUser] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [publicActivity, setPublicActivity] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [countdownEnded, setCountdownEnded] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  /** 与首页 TaskCenter 一致：PUBLISHED + defaultInHome，且 steps 由 buildStepsFromRewards 生成 */
  const steps = useMemo(() => {
    if (!publicActivity) return [];
    return buildStepsFromRewards(publicActivity.rewards, t);
  }, [publicActivity, t]);
  const completedCount = useMemo(() => getCompletedCount(steps), [steps]);
  const hasPublishedActivity = !!publicActivity && steps.length > 0;
  const showOverlay = mounted && notLoginConfirmed && !closedByUser && hasPublishedActivity;
  const maxRewardAmountText = useMemo(() => getMaxRewardAmountText(publicActivity), [publicActivity]);

  const isPermanent = publicActivity?.permanent === true;
  const endTime = publicActivity?.endTime;
  const fallbackTimeText = publicActivity?.timeRangeText ?? "";

  /** 倒计时：与首页已注册 TaskCenter 一致，由 permanent 控制是否展示，由 endTime 做 D:H:M:S */
  useEffect(() => {
    if (isPermanent) {
      setCountdown(null);
      setCountdownEnded(false);
      return;
    }
    if (endTime == null || typeof endTime !== "number") {
      setCountdown(null);
      setCountdownEnded(false);
      return;
    }
    const update = () => {
      const now = Date.now();
      const left = Math.max(0, endTime - now);
      if (left <= 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        setCountdownEnded(true);
        return;
      }
      setCountdownEnded(false);
      const totalSeconds = Math.floor(left / 1000);
      const d = Math.floor(totalSeconds / (24 * 60 * 60));
      const h = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
      const m = Math.floor((totalSeconds % (60 * 60)) / 60);
      const s = totalSeconds % 60;
      setCountdown({ days: d, hours: h, minutes: m, seconds: s });
    };
    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [endTime, isPermanent]);

  /** 未注册：公开接口；活动筛选与首页 TaskCenter 一致（pickPublishedFromList） */
  useEffect(() => {
    if (!mounted || !notLoginConfirmed) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await getPublicActivity();
        if (cancelled) return;
        const list = Array.isArray(result) ? result : result && typeof result === "object" ? [result] : [];
        const picked = pickPublishedFromList(list);
        setPublicActivity(picked);
      } catch (e) {
        if (!cancelled) {
          console.error("[HomeNewUserOverlay] getPublicActivity failed", e);
          setPublicActivity(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mounted, notLoginConfirmed]);

  /* 展示 overlay 时锁定底层页面滚动，并隐藏顶部 navbar（只保留弹层内的关闭按钮） */
  useLayoutEffect(() => {
    if (!showOverlay || typeof document === "undefined") return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.body.setAttribute("data-new-user-overlay-open", "1");

    const styleId = "new-user-overlay-hide-navbar";
    let styleEl = document.getElementById(styleId);
    if (!styleEl) {
      styleEl = document.createElement("style");
      styleEl.id = styleId;
      styleEl.setAttribute("data-overlay", "navbar-hide");
      document.head.appendChild(styleEl);
    }
    styleEl.textContent =
      "body[data-new-user-overlay-open] #__next [class*=\"app-navbar-mb\"]{display:none !important;}";

    return () => {
      document.body.style.overflow = prev;
      document.body.removeAttribute("data-new-user-overlay-open");
      const el = document.getElementById(styleId);
      if (el) el.remove();
    };
  }, [showOverlay]);

  const handleClose = () => {
    setClosedByUser(true);
  };

  if (!showOverlay) return null;

  const registerUrl = getUrl("/accounts/register");

  return (
    <div className={styles.overlay}>
      <header className={styles.header}>
        <button type="button" className={styles.closeBtn} onClick={handleClose} aria-label={t("home.newUserOverlay.close")}>
          <span className={styles.closeIcon} />
        </button>
      </header>

      <div className={styles.noticeStrip}>
        <span className={styles.noticeIcon} aria-hidden>
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M10 17.5C14.1421 17.5 17.5 14.1421 17.5 10C17.5 5.85786 14.1421 2.5 10 2.5C5.85786 2.5 2.5 5.85786 2.5 10C2.5 14.1421 5.85786 17.5 10 17.5ZM7.91667 8.51949C8.49196 8.51949 8.95833 8.05312 8.95833 7.47782C8.95833 6.90253 8.49196 6.43616 7.91667 6.43616C7.34137 6.43616 6.875 6.90253 6.875 7.47782C6.875 8.05312 7.34137 8.51949 7.91667 8.51949ZM7.87231 10.6028C7.87231 11.778 8.82502 12.7307 10.0002 12.7307C11.1755 12.7307 12.1282 11.778 12.1282 10.6028H13.7948C13.7948 12.6985 12.0959 14.3974 10.0002 14.3974C7.90455 14.3974 6.20565 12.6985 6.20565 10.6028H7.87231ZM13.125 7.47782C13.125 8.05312 12.6586 8.51949 12.0833 8.51949C11.508 8.51949 11.0417 8.05312 11.0417 7.47782C11.0417 6.90253 11.508 6.43616 12.0833 6.43616C12.6586 6.43616 13.125 6.90253 13.125 7.47782Z"
              fill="white"
            />
          </svg>
        </span>
        <p className={styles.noticeText}>{t("home.newUserOverlay.notice")}</p>
      </div>

      <main className={styles.main}>
        <div className={styles.illustrationWrap}>
          <img className={styles.illustration} src={giftImg.src || giftImg} alt="" />
        </div>
        {maxRewardAmountText ? <div className={styles.rewardAmount}>{maxRewardAmountText}</div> : null}
        <h2 className={styles.title}>{t("home.newUserOverlay.title")}</h2>

        {!isPermanent ? (
          <div className={styles.countdownRow}>
            <span className={styles.countdownLabel}>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" className={styles.countdownIcon}>
                <path d="M2.54578 9.1667C2.93006 5.69033 5.69033 2.93006 9.1667 2.54578V4.22574C6.61193 4.59116 4.59116 6.61193 4.22574 9.1667H2.54578Z" fill="white" />
                <path d="M10.8334 2.54578V4.22575C13.3881 4.59119 15.4089 6.61195 15.7743 9.1667H17.4542C17.07 5.69035 14.3097 2.93008 10.8334 2.54578Z" fill="white" />
                <path d="M9.1667 15.7743C6.61195 15.4089 4.59119 13.3881 4.22575 10.8334H2.54578C2.93008 14.3097 5.69035 17.07 9.1667 17.4542V15.7743Z" fill="white" />
                <path fillRule="evenodd" clipRule="evenodd" d="M14.1667 10C14.1667 12.3012 12.3012 14.1667 10 14.1667C7.69881 14.1667 5.83333 12.3012 5.83333 10C5.83333 7.69881 7.69881 5.83333 10 5.83333C12.3012 5.83333 14.1667 7.69881 14.1667 10ZM9.375 7.21463V10.5479L11.1427 12.3157L12.0266 11.4318L10.625 10.0303V7.21463H9.375Z" fill="white" />
                <path d="M15.7743 10.8334C15.4088 13.3881 13.3881 15.4088 10.8334 15.7743V17.4542C14.3097 17.0699 17.0699 14.3097 17.4542 10.8334H15.7743Z" fill="#b9f78a" />
              </svg>
              {t("home.newUserOverlay.limitedTime")}
            </span>
            {countdownEnded ? (
              <span className={styles.countdownUnits}>{t("taskCenter.ended")}</span>
            ) : countdown ? (
              <div className={styles.countdownUnits}>
                <span className={styles.countdownUnit}>
                  {countdown.days >= 99 ? "99+" : String(countdown.days).padStart(2, "0")}D
                </span>
                <span className={styles.countdownColon}>:</span>
                <span className={styles.countdownUnit}>{String(countdown.hours).padStart(2, "0")}H</span>
                <span className={styles.countdownColon}>:</span>
                <span className={styles.countdownUnit}>{String(countdown.minutes).padStart(2, "0")}M</span>
                <span className={styles.countdownColon}>:</span>
                <span className={styles.countdownUnit}>{String(countdown.seconds).padStart(2, "0")}S</span>
              </div>
            ) : fallbackTimeText ? (
              <span className={styles.countdownUnits}>{fallbackTimeText}</span>
            ) : null}
          </div>
        ) : null}

        <div className={styles.taskCard}>
          <p className={styles.taskHeader}>{t("home.newUserOverlay.task")}</p>
          <div className={styles.taskList}>
            {steps.map((s, index) => {
              const stepDone = completedCount > index;
              const taskDescText = (s.stepDescription ?? "").trim();
              return (
                <div className={styles.taskItem} key={`${index}-${s.rewardIndex}-${s.stepName ?? ""}`}>
                  <div className={styles.taskDotColumn}>
                    {stepDone ? (
                      <svg className={styles.taskDotSvg} xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden>
                        <circle cx="4" cy="4" r="3" fill="#B9F78A" />
                        <circle cx="4" cy="4" r="3.5" stroke="white" strokeOpacity="0.24" />
                      </svg>
                    ) : (
                      <svg className={styles.taskDotSvg} xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden>
                        <circle cx="4" cy="4" r="3.5" stroke="white" strokeOpacity="0.24" />
                      </svg>
                    )}
                  </div>
                  <div className={styles.taskTextCol}>
                    {taskDescText ? <p className={styles.taskDesc}>{taskDescText}</p> : null}
                  </div>
                </div>
              );
            })}
          </div>
          <div className={styles.taskCardBtnWrap}>
            <span className={styles.taskCardBtnIcon} aria-hidden>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M11.625 6.53125H11.5391V4.57812C11.5391 2.89797 10.1722 1.53125 8.49219 1.53125H7.51562C5.83547 1.53125 4.46875 2.89813 4.46875 4.57812V5.28125C4.46875 5.44701 4.5346 5.60598 4.65181 5.72319C4.76902 5.8404 4.92799 5.90625 5.09375 5.90625C5.25951 5.90625 5.41848 5.8404 5.53569 5.72319C5.6529 5.60598 5.71875 5.44701 5.71875 5.28125V4.57812C5.71933 4.10174 5.90883 3.64504 6.24568 3.30818C6.58254 2.97133 7.03924 2.78183 7.51562 2.78125H8.49219C8.96857 2.78183 9.42528 2.97133 9.76213 3.30818C10.099 3.64504 10.2885 4.10174 10.2891 4.57812V6.53125H4.375C4.02722 6.53228 3.69398 6.67089 3.44806 6.91681C3.20214 7.16273 3.06353 7.49597 3.0625 7.84375V13.1562C3.06353 13.504 3.20214 13.8373 3.44806 14.0832C3.69398 14.3291 4.02722 14.4677 4.375 14.4688H11.625C11.9728 14.4677 12.306 14.3291 12.5519 14.0832C12.7979 13.8373 12.9365 13.504 12.9375 13.1562V7.84375C12.9365 7.49597 12.7979 7.16273 12.5519 6.91681C12.306 6.67089 11.9728 6.53228 11.625 6.53125ZM8.75 11.0122L8.9375 12.5156H7.0625L7.25 11.0122C7.00146 10.8506 6.81181 10.613 6.70932 10.3348C6.60683 10.0566 6.59699 9.75277 6.68126 9.46854C6.76554 9.18432 6.93943 8.93494 7.17699 8.7576C7.41455 8.58025 7.70307 8.48444 7.99953 8.48444C8.29599 8.48444 8.58451 8.58025 8.82207 8.7576C9.05964 8.93494 9.23352 9.18432 9.3178 9.46854C9.40208 9.75277 9.39224 10.0566 9.28975 10.3348C9.18725 10.613 8.9976 10.8506 8.74906 11.0122H8.75Z" fill="currentColor" />
              </svg>
            </span>
            <span className={styles.taskCardBtnText}>{t("home.newUserOverlay.finishAllForReward")}</span>
          </div>
        </div>
      </main>

      <div className={styles.signUpWrap}>
        <a href={registerUrl} className={styles.signUpBtn}>
          {t("home.signUp")}
        </a>
      </div>
    </div>
  );
}
